import dayjs from "dayjs";

/**
 * Homepage showcase for SV City sales to Italy / France.
 * Large UA cities (Київ, Харків, …) may be transfer+feeder; search still uses City.name.
 */

/** Popular UA departure hubs shown on marketing cards / lists (incl. transfer origins). */
export const DEPARTURES = [
  { key: "kyiv", name: "Київ", name_en: "Kyiv", countryKey: "ukraine" },
  { key: "kharkiv", name: "Харків", name_en: "Kharkiv", countryKey: "ukraine" },
  { key: "dnipro", name: "Дніпро", name_en: "Dnipro", countryKey: "ukraine" },
  { key: "lviv", name: "Львів", name_en: "Lviv", countryKey: "ukraine" },
  {
    key: "chernivtsi",
    name: "Чернівці",
    name_en: "Chernivtsi",
    countryKey: "ukraine",
  },
  {
    key: "ivanofrankivsk",
    name: "Івано-Франківськ",
    name_en: "Ivano-Frankivsk",
    countryKey: "ukraine",
  },
];

/** Flagship arrival cities (photo cards use these images). */
export const DESTINATIONS = [
  {
    key: "milan",
    name: "Мілан",
    name_en: "Milan",
    countryKey: "italy",
    image: "/images/cities/milan.jpg",
  },
  {
    key: "genoa",
    name: "Генуя",
    name_en: "Genoa",
    countryKey: "italy",
    image: "/images/cities/genoa.jpg",
  },
  {
    key: "sanremo",
    name: "Санремо",
    name_en: "Sanremo",
    countryKey: "italy",
    image: "/images/cities/sanremo.jpg",
  },
  {
    key: "venice",
    name: "Венеція",
    name_en: "Venice",
    countryKey: "italy",
    image: "/images/cities/venice.jpg",
  },
  {
    key: "nice",
    name: "Ніцца",
    name_en: "Nice",
    countryKey: "france",
    image: "/images/cities/nice.jpg",
  },
  {
    key: "cannes",
    name: "Канни",
    name_en: "Cannes",
    countryKey: "france",
    image: "/images/cities/cannes.jpg",
  },
  {
    key: "monaco",
    name: "Монако",
    name_en: "Monaco",
    countryKey: "monaco",
    image: "/images/cities/monaco.jpg",
  },
  {
    key: "marseille",
    name: "Марсель",
    name_en: "Marseille",
    countryKey: "france",
    image: "/images/cities/marseille.jpg",
  },
];

/** Extra stop cities on the international line (lists / lookups). */
export const EXTRA_ROUTE_CITIES = [
  { key: "kolomyia", name: "Коломия", name_en: "Kolomyia", countryKey: "ukraine" },
  { key: "stryi", name: "Стрий", name_en: "Stryi", countryKey: "ukraine" },
  { key: "mukachevo", name: "Мукачево", name_en: "Mukachevo", countryKey: "ukraine" },
  { key: "verona", name: "Верона", name_en: "Verona", countryKey: "italy" },
  { key: "bergamo", name: "Бергамо", name_en: "Bergamo", countryKey: "italy" },
  { key: "brescia", name: "Брешія", name_en: "Brescia", countryKey: "italy" },
  { key: "vicenza", name: "Віченца", name_en: "Vicenza", countryKey: "italy" },
  { key: "trieste", name: "Трієсте", name_en: "Trieste", countryKey: "italy" },
  { key: "savona", name: "Савона", name_en: "Savona", countryKey: "italy" },
  { key: "antibes", name: "Антіби", name_en: "Antibes", countryKey: "france" },
  { key: "frejus", name: "Фрежус", name_en: "Fréjus", countryKey: "france" },
  {
    key: "saintTropez",
    name: "Сан-Тропе",
    name_en: "Saint-Tropez",
    countryKey: "france",
  },
  { key: "toulon", name: "Тулон", name_en: "Toulon", countryKey: "france" },
];

/**
 * Photo cards: popular OD pairs (incl. transfer origins like Київ / Харків).
 * Image = arrival city.
 */
export const FEATURED_ROUTE_CARDS = [
  { fromKey: "kyiv", toKey: "milan" },
  { fromKey: "kharkiv", toKey: "venice" },
  { fromKey: "lviv", toKey: "sanremo" },
  { fromKey: "dnipro", toKey: "genoa" },
  { fromKey: "kyiv", toKey: "nice" },
  { fromKey: "kharkiv", toKey: "marseille" },
  { fromKey: "lviv", toKey: "cannes" },
  { fromKey: "chernivtsi", toKey: "milan" },
];

/**
 * Quick-route lists: Italy + France only (UA feeder legs are not listed).
 * Mix of large hubs (incl. transfer) and real international-line stops.
 */
export const POPULAR_ROUTES_BY_COUNTRY = [
  {
    countryKey: "italy",
    routes: [
      { fromKey: "kyiv", toKey: "milan" },
      { fromKey: "kharkiv", toKey: "venice" },
      { fromKey: "dnipro", toKey: "genoa" },
      { fromKey: "lviv", toKey: "sanremo" },
      { fromKey: "chernivtsi", toKey: "milan" },
      { fromKey: "ivanofrankivsk", toKey: "venice" },
      { fromKey: "kolomyia", toKey: "verona" },
      { fromKey: "stryi", toKey: "bergamo" },
      { fromKey: "mukachevo", toKey: "brescia" },
      { fromKey: "lviv", toKey: "trieste" },
      { fromKey: "chernivtsi", toKey: "vicenza" },
      { fromKey: "kyiv", toKey: "sanremo" },
      { fromKey: "kharkiv", toKey: "genoa" },
      { fromKey: "ivanofrankivsk", toKey: "savona" },
      { fromKey: "mukachevo", toKey: "milan" },
      { fromKey: "lviv", toKey: "bergamo" },
    ],
  },
  {
    countryKey: "france",
    routes: [
      { fromKey: "kyiv", toKey: "nice" },
      { fromKey: "kharkiv", toKey: "marseille" },
      { fromKey: "dnipro", toKey: "cannes" },
      { fromKey: "lviv", toKey: "monaco" },
      { fromKey: "chernivtsi", toKey: "marseille" },
      { fromKey: "ivanofrankivsk", toKey: "nice" },
      { fromKey: "kolomyia", toKey: "antibes" },
      { fromKey: "stryi", toKey: "frejus" },
      { fromKey: "mukachevo", toKey: "saintTropez" },
      { fromKey: "lviv", toKey: "toulon" },
      { fromKey: "chernivtsi", toKey: "cannes" },
      { fromKey: "kyiv", toKey: "marseille" },
      { fromKey: "kharkiv", toKey: "nice" },
      { fromKey: "ivanofrankivsk", toKey: "antibes" },
      { fromKey: "lviv", toKey: "cannes" },
      { fromKey: "mukachevo", toKey: "nice" },
    ],
  },
];

/** Flat list (back-compat). */
export const QUICK_ROUTES = POPULAR_ROUTES_BY_COUNTRY.flatMap((g) => g.routes);

export function getCityByKey(key) {
  return (
    DEPARTURES.find((c) => c.key === key) ||
    DESTINATIONS.find((c) => c.key === key) ||
    EXTRA_ROUTE_CITIES.find((c) => c.key === key)
  );
}

export function findCityByName(name) {
  const n = String(name || "")
    .trim()
    .toLowerCase();
  if (!n) return null;
  const all = [...DEPARTURES, ...DESTINATIONS, ...EXTRA_ROUTE_CITIES];
  return (
    all.find(
      (c) =>
        c.name.toLowerCase() === n ||
        (c.name_en && c.name_en.toLowerCase() === n)
    ) || null
  );
}

/**
 * Related destinations strip: other flagship IT/FR cities on own network.
 */
export function relatedDestinationsForToCity(toCityName) {
  const city = findCityByName(toCityName);
  return {
    mode: "flagship",
    countryKey: null,
    items: DESTINATIONS.filter(
      (d) =>
        d.countryKey !== "monaco" && (!city || d.key !== city.key)
    ),
  };
}

export function getDepartureByKey(key) {
  return getCityByKey(key);
}

export function getDestinationByKey(key) {
  return getCityByKey(key);
}

export function destinationsByCountry(countryKey) {
  if (!countryKey || countryKey === "all") return DESTINATIONS;
  return DESTINATIONS.filter((d) => d.countryKey === countryKey);
}

export function resolveFeaturedCard(card) {
  const from = getCityByKey(card.fromKey);
  const to = getCityByKey(card.toKey);
  if (!from || !to) return null;
  return {
    fromKey: card.fromKey,
    toKey: card.toKey,
    from,
    to,
    image: to.image || from.image || null,
  };
}

/**
 * Build homepage search URL with canonical UA city names.
 * Pass only `toName` (or only `fromName`) to prefill the search bar without searching.
 */
export function buildSearchUrl({ fromName, toName, date, returnDate }) {
  const params = new URLSearchParams();
  if (fromName) params.set("from", fromName);
  if (toName) params.set("to", toName);
  if (fromName && toName) {
    const d = (date || dayjs()).format("YYYY-MM-DD");
    params.set("date", d);
    params.set("adults", "1");
    params.set("children", "0");
    if (returnDate) {
      params.set("return", dayjs(returnDate).format("YYYY-MM-DD"));
    }
  } else {
    params.set("focus", fromName ? "to" : "from");
  }
  return `/?${params.toString()}`;
}
